import { WorkSection, WorkEmpty } from './work-page.tsx';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  VISIT_TYPES,
  saveChecklistStackSchema,
  type ChecklistLayer,
  type ChecklistStack,
  type ChecklistVisitType,
} from '@shared/checklists/stack.ts';
import { checklistStackApi, scheduleApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { usePlanFeature } from './plan-gate.tsx';
import { Alert, Button, LoadingBlock, Select, TextInput } from './ui.tsx';
import { ChecklistDefinitionEditor, newChecklistDefinition } from './checklist-definition-editor.tsx';
import { useChecklistEditScope } from './checklist-edit-scope.ts';
import { useLocalizedContent } from '../i18n/localized.ts';
import { formatNumericDate } from '../i18n/datetime.ts';

export function PropertyChecklistStack({ propertyId, customerId }: { propertyId: string; customerId: string }) {
  const { t } = useTranslation();
  const { can } = useSession();
  const scopeGuard = useChecklistEditScope('customers:write');
  const localized = useLocalizedContent();
  const queryClient = useQueryClient();
  const photoAllowed = usePlanFeature('checklist_reports');
  const request = useRef(crypto.randomUUID());
  const stack = useQuery({
    queryKey: ['checklist-stack', propertyId],
    queryFn: () => checklistStackApi.stack(propertyId),
  });
  const schedules = useQuery({
    queryKey: ['property-checklist-schedules', propertyId, customerId],
    queryFn: async () => {
      const first = await scheduleApi.listSeries({ customerId, pageSize: 100 });
      const items = [...first.items];
      for (let page = 2; page <= first.totalPages; page++)
        items.push(...(await scheduleApi.listSeries({ customerId, pageSize: 100, page })).items);
      return { ...first, items };
    },
  });
  const templates = useQuery({ queryKey: ['checklist-templates'], queryFn: checklistStackApi.templates });
  const [draft, setDraft] = useState<ChecklistStack | null>(null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshed, setRefreshed] = useState<number | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [visitType, setVisitType] = useState<ChecklistVisitType | null>(null);
  const [ordinal, setOrdinal] = useState(1);
  const [seriesId, setSeriesId] = useState('');
  const [previewing, setPreviewing] = useState(false);
  useEffect(() => {
    if (stack.data && !dirty) setDraft(stack.data.stack);
  }, [stack.data, dirty]);
  const preview = useQuery({
    queryKey: ['checklist-stack-preview', propertyId, date, visitType, ordinal, seriesId, stack.data?.stack.version],
    queryFn: () => checklistStackApi.preview(propertyId, date, visitType, ordinal, seriesId || undefined),
    enabled: previewing && !dirty,
  });
  const change = (layers: ChecklistLayer[]) => {
    setDraft({ version: draft?.version ?? 0, layers });
    setDirty(true);
    setPreviewing(false);
    request.current = crypto.randomUUID();
  };
  const save = useMutation({
    mutationFn: async () => {
      if (!scopeGuard.allowed) throw new Error('The checklist edit scope changed.');
      const editScope = scopeGuard.stamp;
      const result = await checklistStackApi.saveStack(propertyId, {
        requestId: request.current,
        version: draft?.version ?? 0,
        layers: draft?.layers ?? [],
      });
      return { ...result, editScope };
    },
    onSuccess: async (result) => {
      if (!scopeGuard.isCurrent(result.editScope)) return;
      setDraft(result.stack);
      setDirty(false);
      setRefreshed(result.refreshed);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['checklist-stack', propertyId] });
      await queryClient.invalidateQueries({ queryKey: ['visit-checklist'] });
    },
    onError: (caught) =>
      scopeGuard.isCurrent() &&
      setError(
        t(
          caught instanceof ApiRequestError && caught.status === 409
            ? 'checklistStack.stale'
            : 'checklistStack.saveError',
        ),
      ),
  });
  const editable = scopeGuard.allowed && can('customers:write') && !save.isPending;
  const layers = draft?.layers ?? [];
  const types = [
    { value: '', label: t('checklistStack.allTypes') },
    ...VISIT_TYPES.map((type) => ({ value: type, label: t(`checklistStack.types.${type}`) })),
  ];
  const reorder = (from: number, to: number) => {
    if (from === to || to < 0 || to >= layers.length) return;
    const next = [...layers];
    const [layer] = next.splice(from, 1);
    next.splice(to, 0, layer!);
    change(next);
  };
  if (!scopeGuard.sameScope) return <Alert tone="error">{t('checklistStack.stale')}</Alert>;
  return (
    <WorkSection
      title={t('checklistStack.propertyHeading')}
      description={t('checklistStack.stackHint')}
      actions={
        <Link className="btn btn-secondary" to="/checklist-templates">
          {t('checklistStack.library')}
        </Link>
      }
    >
      <div className="space-y-4">
        <p className="text-sm">{t('checklistStack.copyHint')}</p>
        {error && <Alert tone="error">{error}</Alert>}
        {refreshed !== null && <Alert tone="success">{t('checklistStack.refreshed', { count: refreshed })}</Alert>}
        {stack.isError || templates.isError ? <Alert tone="error">{t('checklistStack.loadError')}</Alert> : null}
        {stack.isLoading ? <LoadingBlock /> : null}
        {!stack.isLoading && !stack.isError && draft && layers.length === 0 ? (
          <WorkEmpty title={t('checklistStack.propertyHeading')}>
            <p>{t('checklistStack.stackHint')}</p>
          </WorkEmpty>
        ) : null}
        <ol className="space-y-3">
          {layers.map((layer, index) => (
            <li
              key={layer.id}
              className="space-y-3 rounded-xl border border-[color:var(--color-line)] p-3"
              draggable={editable}
              onDragStart={(event) => event.dataTransfer.setData('text/plain', String(index))}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                if (editable) {
                  const from = Number(event.dataTransfer.getData('text/plain'));
                  if (Number.isInteger(from) && from >= 0 && from < layers.length) reorder(from, index);
                }
              }}
            >
              <h3 className="font-medium">
                {index + 1}.{' '}
                {layer.templateId
                  ? localized.text(
                      templates.data?.templates.find((template) => template.id === layer.templateId)?.definition.name ??
                        t('checklistStack.template'),
                      templates.data?.templates.find((template) => template.id === layer.templateId)?.definition
                        .nameTranslations ?? null,
                    )
                  : layer.definition
                    ? localized.text(
                        layer.definition.name || t('checklistStack.propertyTasks'),
                        layer.definition.nameTranslations,
                      )
                    : t('checklistStack.existingTasks')}
              </h3>
              <Select
                label={t('checklistStack.visitType')}
                options={types}
                value={layer.visitType ?? ''}
                disabled={!editable}
                onChange={(event) =>
                  change(
                    layers.map((current) =>
                      current.id === layer.id
                        ? { ...current, visitType: (event.target.value || null) as ChecklistVisitType | null }
                        : current,
                    ),
                  )
                }
              />
              {layer.definition && (
                <ChecklistDefinitionEditor
                  value={layer.definition}
                  photoAllowed={photoAllowed}
                  disabled={!editable}
                  onChange={(definition) =>
                    change(layers.map((current) => (current.id === layer.id ? { ...current, definition } : current)))
                  }
                />
              )}
              <div className="flex flex-wrap gap-2">
                <Button disabled={!editable || index === 0} onClick={() => reorder(index, index - 1)}>
                  {t('checklistStack.up')}
                </Button>
                <Button disabled={!editable || index === layers.length - 1} onClick={() => reorder(index, index + 1)}>
                  {t('checklistStack.down')}
                </Button>
                <Button
                  disabled={!editable}
                  onClick={() => change(layers.filter((current) => current.id !== layer.id))}
                >
                  {t('common.remove')}
                </Button>
              </div>
            </li>
          ))}
        </ol>
        <div className="flex flex-wrap items-end gap-2">
          <Select
            label={t('checklistStack.template')}
            value={templateId}
            disabled={!editable}
            onChange={(event) => setTemplateId(event.target.value)}
            options={[
              { value: '', label: t('checklistStack.chooseTemplate') },
              ...(templates.data?.templates.map((template) => ({
                value: template.id,
                label: localized.text(template.definition.name, template.definition.nameTranslations),
              })) ?? []),
            ]}
          />
          <Button
            disabled={!editable || !templateId || layers.length >= 20}
            onClick={() =>
              change([...layers, { id: crypto.randomUUID(), templateId, visitType: null, definition: null }])
            }
          >
            {t('checklistStack.addTemplate')}
          </Button>
          <Button
            disabled={!editable || layers.length >= 20}
            onClick={() =>
              change([
                ...layers,
                { id: crypto.randomUUID(), templateId: null, visitType: null, definition: newChecklistDefinition() },
              ])
            }
          >
            {t('checklistStack.addPropertyTasks')}
          </Button>
          <Button
            disabled={!editable || layers.length >= 20}
            onClick={() =>
              change([...layers, { id: crypto.randomUUID(), templateId: null, visitType: null, definition: null }])
            }
          >
            {t('checklistStack.addExisting')}
          </Button>
        </div>
        <Alert tone="info">{t('checklistStack.refreshHint')}</Alert>
        <Button
          variant="primary"
          busy={save.isPending}
          disabled={!editable || !dirty || !draft}
          onClick={() => {
            if (!saveChecklistStackSchema.safeParse({ ...draft, requestId: request.current }).success) {
              setError(t('checklistStack.invalid'));
              return;
            }
            save.mutate();
          }}
        >
          {t('checklistStack.saveStack')}
        </Button>
        <section className="checklist-work-preview space-y-3" aria-label={t('checklistStack.previewDate')}>
          <h3 className="font-semibold">{t('checklistStack.previewDate')}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextInput
              label={t('checklistStack.previewDate')}
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
            <Select
              label={t('checklistStack.visitType')}
              value={visitType ?? ''}
              options={types}
              onChange={(event) => setVisitType((event.target.value || null) as ChecklistVisitType | null)}
            />
            <TextInput
              label={t('checklistStack.ordinal')}
              type="number"
              min={1}
              value={ordinal}
              onChange={(event) => setOrdinal(Number(event.target.value))}
            />
            <Select
              label={t('checklistStack.schedule')}
              value={seriesId}
              options={[
                { value: '', label: t('checklistStack.singlePreview') },
                ...(schedules.data?.items
                  .filter((series) => series.propertyId === propertyId)
                  .map((series) => ({
                    value: series.id,
                    label: `${t(`servicesStep.frequency.${series.frequency}`)} (${formatNumericDate(series.startsOn)})`,
                  })) ?? []),
              ]}
              onChange={(event) => setSeriesId(event.target.value)}
            />
          </div>
          <p className="text-sm text-[color:var(--color-muted)]">{t('checklistStack.previewHint')}</p>
          <Button disabled={dirty || !date || ordinal < 1} onClick={() => setPreviewing(true)}>
            {t('checklistStack.preview', { date: date ? formatNumericDate(date) : '' })}
          </Button>
          {preview.isError && <Alert tone="error">{t('checklistStack.loadError')}</Alert>}
          {previewing && !dirty && (
            <ul className="space-y-1">
              {preview.data?.tasks
                .filter((task) => task.due)
                .map((task) => (
                  <li key={task.sourceKey} className="text-sm">
                    {localized.text(task.areaName, task.areaNameTranslations)}:{' '}
                    {localized.text(task.taskName, task.taskNameTranslations)}
                  </li>
                ))}
            </ul>
          )}
        </section>
      </div>
    </WorkSection>
  );
}
